/** Dates and numbers go through Intl, never through copy.ts. */

/**
 * Fixed to Swedish, not the device's language.
 *
 * Every string the app writes itself is Swedish (`copy.ts`), and the household
 * is in Sweden — so a member whose phone is set to English was getting an
 * AM/PM clock and English weekday names ("Monday") stitched into Swedish copy
 * ("Mondayar" instead of "måndagar"). `navigator.language` was the wrong source
 * for a single-language app; `sv-SE` is a 24-hour, Swedish-named locale, which
 * is what the app has always meant. The tests already pinned this.
 */
const LOCALE = "sv-SE";
export const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * **Every formatter here takes a zone, defaulting to the device's.**
 *
 * The default is not laziness: a task's `dueAt` is deliberately read in the
 * zone of whoever is looking, which for a household in one place is the same
 * zone anyway. The calendar is the caller that cannot use it — a shared
 * calendar has to say the same thing on every phone in the family, so it passes
 * `households.timezone` explicitly. Both go through one implementation, so the
 * two readings cannot drift apart the way two copies would.
 */
export const time = (d: Date, tz = TZ) =>
  new Intl.DateTimeFormat(LOCALE, { hour: "numeric", minute: "2-digit", timeZone: tz }).format(d);

/** Plain counts — group sizes, badges. Still Intl, never String(n). */
export const count = (n: number) => new Intl.NumberFormat(LOCALE).format(n);

export const dayLong = (d: Date, tz = TZ) =>
  new Intl.DateTimeFormat(LOCALE, { weekday: "long", day: "numeric", month: "long", timeZone: tz }).format(d);

/**
 * A day header for a list of events: "fre 21 aug.", with the year only when it
 * is not this one. Short rather than `dayLong` because it repeats down a
 * screen, and a header is scanned, not read.
 */
export const dayShort = (d: Date, tz = TZ, now = new Date()) =>
  new Intl.DateTimeFormat(LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: yearIn(d, tz) === yearIn(now, tz) ? undefined : "numeric",
    timeZone: tz,
  }).format(d);

const yearIn = (d: Date, tz: string) =>
  new Intl.DateTimeFormat("en-CA", { year: "numeric", timeZone: tz }).format(d);

/**
 * The weekday alone — "tisdag" — for describing a weekly recurrence.
 *
 * `rrule` can describe its own rules, but only in English, so the sentence is
 * assembled in `copy.ts` and the names come from here. Nothing in the app ever
 * spells a weekday out itself.
 */
export const weekday = (d: Date, tz = TZ) =>
  new Intl.DateTimeFormat(LOCALE, { weekday: "long", timeZone: tz }).format(d);

/** The day of the month alone — "31" — for a monthly recurrence. */
export const monthDay = (d: Date, tz = TZ) =>
  new Intl.DateTimeFormat(LOCALE, { day: "numeric", timeZone: tz }).format(d);

/** Day and month, no year — "3 juni" — for a yearly one. */
export const dayAndMonth = (d: Date, tz = TZ) =>
  new Intl.DateTimeFormat(LOCALE, { day: "numeric", month: "long", timeZone: tz }).format(d);

/** The month a screen is showing — "augusti 2026". The calendar's own title. */
export const monthLong = (d: Date, tz = TZ) =>
  new Intl.DateTimeFormat(LOCALE, { month: "long", year: "numeric", timeZone: tz }).format(d);

/**
 * Which weekday a week starts on here, 0 = Sunday, counting as
 * `Date#getUTCDay` does.
 *
 * A month grid that starts on the wrong day is wrong in a way nobody misreads
 * as a bug — it just quietly disagrees with every other calendar on the phone —
 * so it is asked of the locale rather than assumed.
 *
 * `getWeekInfo` is recent and not everywhere: Node has no implementation at
 * all, and neither do Safari before 17 or Chrome before 130. Monday is the
 * fallback, which is the right answer for `sv-SE` and for every locale this
 * app is actually read in; the cast is because the method predates the type
 * definitions it should be in.
 */
export function weekStart(): number {
  const info = (new Intl.Locale(LOCALE) as unknown as {
    getWeekInfo?: () => { firstDay: number };
  }).getWeekInfo?.();
  // `firstDay` is ISO — 1 Monday through 7 Sunday — and everything else here
  // counts Sunday as 0, which is the whole of what the modulo does.
  return info === undefined ? 1 : info.firstDay % 7;
}

/**
 * The seven column headings of a month grid, in order from `weekStartsOn`.
 *
 * Short rather than narrow: narrow gives Swedish two indistinguishable "T"s
 * for tisdag and torsdag, and at 390px a column is 51px wide, which "tis" fits
 * inside comfortably.
 *
 * 7 January 2024 was a Sunday. It is read in UTC and never displayed — these
 * are names of weekdays, not of days, and the date is only the handle Intl
 * needs to produce one.
 *
 * **Noon rather than midnight**, which is belt as well as braces. The zone is
 * given explicitly, so this is already right; anchoring at midnight would mean
 * that dropping the `timeZone` — or any future edit that reaches for the
 * device's — silently rotates every column heading by one for the whole
 * western hemisphere, where 00:00Z is still the previous evening. A mutation
 * test found exactly that and could not kill it from a machine pinned to
 * Stockholm, so the failure mode is removed here instead of watched for.
 */
export function weekdayNames(weekStartsOn: number): string[] {
  const format = new Intl.DateTimeFormat(LOCALE, { weekday: "short", timeZone: "UTC" });
  const sunday = Date.UTC(2024, 0, 7, 12);
  return Array.from({ length: 7 }, (_, i) =>
    format.format(new Date(sunday + ((weekStartsOn + i) % 7) * 864e5)),
  );
}

/** Midnight local to `tz`, as an epoch ms, for the calendar day `d` falls on. */
function startOfDay(d: Date, tz = TZ) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit", timeZone: tz,
  }).format(d);
  return new Date(`${parts}T00:00:00`).getTime();
}

/** Whole calendar days from today to `d`. Negative is the past. */
export function daysUntil(d: Date, now = new Date(), tz = TZ) {
  return Math.round((startOfDay(d, tz) - startOfDay(now, tz)) / 864e5);
}

/**
 * A due date as a person would say it.
 *
 * Within a day either side, Intl's relative formatter says "i dag" / "i morgon"
 * on its own — that is why `numeric: "auto"` exists, and why those words are not
 * in copy.ts. Beyond that a real date is more use than "in 9 days", and the year
 * only appears when it isn't this one.
 *
 * Comparison is by calendar day, not elapsed hours: something due in 20 hours
 * is due "i morgon" if it crosses midnight, not "i dag".
 */
export function dueDate(dueAt: number, now = new Date(), tz = TZ) {
  const due = new Date(dueAt);
  const days = daysUntil(due, now, tz);

  if (Math.abs(days) <= 1) {
    return new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" }).format(days, "day");
  }

  return new Intl.DateTimeFormat(LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    // `getFullYear` would answer in the device's zone even when `tz` is not it.
    year: yearIn(due, tz) === yearIn(now, tz) ? undefined : "numeric",
    timeZone: tz,
  }).format(due);
}

/** Which colour a due line takes: alert when late, mark on the day, else muted. */
export type DueTone = "overdue" | "today" | "soon";

/**
 * The due line under a task's title, and its tone.
 *
 *   overdue  → "Försenad · tors 24 sep."   the word, then the date it was due
 *   today    → "i dag"
 *   later    → "i morgon", then a date     (as `dueDate`)
 *
 * Overdue always names the date rather than "i går": "late" is the fact that
 * matters and the date says by how much, where "i går · Försenad" would say it
 * twice. The word is copy, so it is passed in — this file formats, it does not
 * speak.
 */
export function dueLine(
  dueAt: number,
  overdueWord: string,
  now = new Date(),
  tz = TZ,
): { tone: DueTone; text: string } {
  const days = daysUntil(new Date(dueAt), now, tz);
  if (days < 0) {
    const date = new Intl.DateTimeFormat(LOCALE, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: yearIn(new Date(dueAt), tz) === yearIn(now, tz) ? undefined : "numeric",
      timeZone: tz,
    }).format(new Date(dueAt));
    return { tone: "overdue", text: `${overdueWord} · ${date}` };
  }
  return { tone: days === 0 ? "today" : "soon", text: dueDate(dueAt, now, tz) };
}

/** `YYYY-MM-DD` in `tz` — the value shape `<input type="date">` wants. */
export const dateInputValue = (at: number, tz = TZ) =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit", timeZone: tz,
  }).format(new Date(at));

/**
 * The inverse: a `YYYY-MM-DD` from the date input back to an instant.
 *
 * Anchored to the end of that day, so a task due "Thursday" stays due all of
 * Thursday instead of falling overdue at breakfast.
 */
export const dateInputToMs = (value: string) => new Date(`${value}T23:59:59`).getTime();

/**
 * `YYYY-MM-DDTHH:mm` in `tz` — what `<input type="datetime-local">` wants.
 *
 * The input has no concept of a zone, so this is the instant *rendered* in the
 * event's zone. Its inverse below puts one back.
 */
export function dateTimeInputValue(at: number, tz = TZ) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
    timeZone: tz,
  }).formatToParts(new Date(at));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)!.value;
  // `hour12: false` yields "24" for midnight in some ICU versions, never "00".
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
}

/**
 * A `YYYY-MM-DDTHH:mm` wall-clock reading in `tz`, back to an instant.
 *
 * `new Date(value)` would read it in the *device's* zone, which is wrong the
 * moment those differ — an event typed as 08:30 in Stockholm would be stored as
 * 08:30 wherever the phone happens to be. So the offset `tz` was actually at
 * that moment is measured and subtracted. Measured rather than assumed because
 * it changes twice a year.
 */
export function dateTimeInputToMs(value: string, tz = TZ) {
  const naive = new Date(`${value}:00Z`).getTime();
  return naive - offsetAt(naive, tz);
}

/**
 * How far `tz` is from UTC at a given instant, in ms.
 *
 * Formatting the instant *as* that zone and reading it back as if it were UTC
 * gives the shifted clock; the difference is the offset. Two passes because the
 * offset can itself change across the boundary being probed, which is the one
 * hour a year the naive version is wrong.
 */
function offsetAt(at: number, tz: string) {
  const read = (instant: number) => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      timeZone: tz,
    }).formatToParts(new Date(instant));
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)!.value;
    const hour = get("hour") === "24" ? "00" : get("hour");
    return Date.parse(
      `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}:${get("second")}Z`,
    );
  };

  const first = read(at) - at;
  return read(at - first) - (at - first);
}

/**
 * A minutes-from-local-midnight time of day — 600 → "10:00".
 *
 * A servicedag window is a wall-clock reading with no instant behind it, so it
 * is anchored to a fixed UTC date at that hour and read back in UTC — the same
 * "names of times, not times" trick `weekdayNames` uses for column headings.
 */
export const timeOfDay = (minutes: number) =>
  new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" })
    .format(Date.UTC(2024, 0, 1, Math.floor(minutes / 60), minutes % 60));

/** A window as one string — "10:00–14:00". */
export const timeWindow = (startMinute: number, endMinute: number) =>
  `${timeOfDay(startMinute)}–${timeOfDay(endMinute)}`;

/** The `<input type="time">` value — "HH:mm", zero-padded — for a minutes count. */
export const minutesToTimeInput = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** The inverse: "HH:mm" from the time input back to minutes from midnight. */
export const timeInputToMinutes = (value: string) => {
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
};

/**
 * The name of a weekday given as 0 = Monday … 6 = Sunday — "torsdag".
 *
 * That numbering is the servicedag schema's, not `Date`'s. Anchored to
 * 2024-01-01, which was a Monday, and read in UTC: these are names of weekdays,
 * not of days, so no instant and no zone is involved.
 */
export const weekdayName = (index: number) =>
  new Intl.DateTimeFormat(LOCALE, { weekday: "long", timeZone: "UTC" })
    .format(new Date(Date.UTC(2024, 0, 1 + index)));

/** "in 2 days" / "3 hours ago" */
export function relative(d: Date, now = new Date()) {
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });
  const diff = d.getTime() - now.getTime();
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["day", 864e5], ["hour", 36e5], ["minute", 6e4],
  ];
  for (const [unit, ms] of units) {
    if (Math.abs(diff) >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  return rtf.format(0, "minute");
}
